import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Download, Eye, Link as LinkIcon, Trash2, 
  Loader2, AlertCircle, Play, Image as ImageIcon,
  CheckCircle, Clock, Film, Sparkles, Zap, Wand2, Video,
  Send, Rocket, Palette, Edit, Pencil
} from 'lucide-react';
import { formatFileSize } from '../../../lib/contentHelpers';
import { contentService } from '../../../services/ContentService';

// Ícones SVG das plataformas
const InstagramIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// Ícone do Placid
const PlacidIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 14l-5-5 1.41-1.41L12 14.17l4.59-4.58L18 11l-6 6z"/>
  </svg>
);

// ============================================
// ESTILOS CSS INLINE PARA ANIMAÇÕES
// ============================================
const styleSheet = `
  @keyframes gradientFlow {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }

  @keyframes shimmerWave {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(100%);
    }
  }

  @keyframes pulseGlow {
    0%, 100% {
      opacity: 0.6;
      transform: scale(1);
    }
    50% {
      opacity: 0.8;
      transform: scale(1.02);
    }
  }

  @keyframes breathe {
    0%, 100% {
      opacity: 0.4;
    }
    50% {
      opacity: 0.7;
    }
  }

  @keyframes floatUp {
    0%, 100% {
      transform: translateY(0px);
    }
    50% {
      transform: translateY(-4px);
    }
  }

  .animate-gradient-flow {
    background-size: 200% 200%;
    animation: gradientFlow 3s ease infinite;
  }

  .animate-shimmer-wave {
    animation: shimmerWave 2s ease-in-out infinite;
  }

  .animate-pulse-glow {
    animation: pulseGlow 2.5s ease-in-out infinite;
  }

  .animate-breathe {
    animation: breathe 3s ease-in-out infinite;
  }

  .animate-float {
    animation: floatUp 3s ease-in-out infinite;
  }
`;

// Injetar estilos no documento (uma vez)
if (typeof document !== 'undefined') {
  const existingStyle = document.getElementById('content-card-animations');
  if (!existingStyle) {
    const styleElement = document.createElement('style');
    styleElement.id = 'content-card-animations';
    styleElement.textContent = styleSheet;
    document.head.appendChild(styleElement);
  }
}

const ContentCard = ({ content, gridSpan, onDelete, onPreview, onPublish, batchId }) => {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);
  const [imageError, setImageError] = useState(false);

  const modelBadge = contentService.helpers.getModelBadge(content.model);

  // Verificar se é conteúdo editável (Placid, Creatomate, Templated ou React Render)
  const isPlacidContent = content.model === 'placid';
  const isCreatomateContent = content.model === 'creatomate';
  const isTemplatedContent = content.model === 'templated';
  const isReactRenderContent = content.model === 'react_render';
  // ✅ Fase 4: gpt_image é editável via chat conversacional (não via rota)
  const isGptImageContent = content.model === 'gpt_image';
  const isGptImageEditable = isGptImageContent && content.status === 'completed';
  // ✅ Fase 4: edição em andamento neste content?
  const isEditingNow = content.metadata?.edit_job?.status === 'processing';
  const isEditable = (
    isPlacidContent ||
    (isCreatomateContent && content.metadata?.render_script) ||
    (isTemplatedContent && content.metadata?.template_id && content.metadata?.layers) ||
    (isReactRenderContent && content.metadata?.jsx_template && content.metadata?.editable_fields)
  ) && content.status === 'completed';
  const hasEditHistory = content.edit_history && content.edit_history.length > 0;

  // Verificar onde foi publicado
  const publishedPlatforms = content.published_to || [];
  const isPublishedOnInstagram = publishedPlatforms.includes('instagram');
  const isPublishedOnTikTok = publishedPlatforms.includes('tiktok');
  const isPublishedAnywhere = publishedPlatforms.length > 0;

  // Navegar para o editor (Placid, Creatomate, Templated ou React Render)
  const handleEdit = (e) => {
    e.stopPropagation();
    if (isEditable && batchId) {
      if (isCreatomateContent) {
        navigate(`/contents/batch/${batchId}/edit-creatomate/${content.id}`);
      } else if (isTemplatedContent) {
        navigate(`/contents/batch/${batchId}/edit-templated/${content.id}`);
      } else if (isReactRenderContent) {
        navigate(`/contents/batch/${batchId}/edit-react-render/${content.id}`);
      } else {
        navigate(`/contents/batch/${batchId}/edit/${content.id}`);
      }
    }
  };

  // Copiar URL para clipboard
  const handleCopyUrl = async () => {
    if (content.result_url) {
      try {
        await navigator.clipboard.writeText(content.result_url);
        alert('Link copiado para a área de transferência!');
      } catch (err) {
        console.error('Erro ao copiar:', err);
      }
    }
  };

  // Download do arquivo (via fetch+blob para forçar download cross-origin)
  const handleDownload = async () => {
    if (!content.result_url) return;
    try {
      const response = await fetch(content.result_url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = content.file_name || `content_${content.id}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      window.open(content.result_url, '_blank');
    }
  };

  // Handler para publicar
  const handlePublish = (e) => {
    e.stopPropagation();
    if (onPublish) {
      onPublish(content);
    }
  };

  // ============================================
  // ESTADO: PENDING (Na fila)
  // Design: Gradiente suave com pulse elegante
  // ============================================
  if (content.status === 'pending') {
    return (
      <div
        className={`${gridSpan.col} ${gridSpan.row} relative rounded-2xl overflow-hidden min-h-[200px]`}
      >
        {/* Fundo com gradiente suave */}
        <div 
          className="absolute inset-0 animate-pulse-glow"
          style={{
            background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 25%, #fbbf24 50%, #f59e0b 75%, #fbbf24 100%)',
            opacity: 0.7
          }}
        />
        
        {/* Overlay com blur effect */}
        <div 
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse at center, rgba(251, 191, 36, 0.3) 0%, rgba(245, 158, 11, 0.5) 100%)',
            backdropFilter: 'blur(60px)'
          }}
        />

        {/* Borda sutil */}
        <div 
          className="absolute inset-0 rounded-2xl pointer-events-none"
          style={{
            border: '1px solid rgba(251, 191, 36, 0.3)'
          }}
        />
      </div>
    );
  }

  // ============================================
  // ESTADO: PROCESSING (Gerando)
  // Design: Efeito blur com gradiente animado (estilo ChatGPT)
  // ============================================
  if (content.status === 'processing') {
    return (
      <div
        className={`${gridSpan.col} ${gridSpan.row} relative rounded-2xl overflow-hidden min-h-[200px]`}
      >
        {/* Camada 1: Gradiente base animado */}
        <div 
          className="absolute inset-0 animate-gradient-flow"
          style={{
            background: 'linear-gradient(-45deg, #fbbf24, #f59e0b, #d97706, #fbbf24, #fde68a, #fbbf24)',
            backgroundSize: '400% 400%'
          }}
        />

        {/* Camada 2: Overlay com efeito de profundidade */}
        <div 
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(ellipse at 30% 20%, rgba(254, 243, 199, 0.4) 0%, transparent 50%), radial-gradient(ellipse at 70% 80%, rgba(217, 119, 6, 0.3) 0%, transparent 50%)'
          }}
        />

        {/* Camada 3: Efeito de blur/névoa (simula imagem nascendo) */}
        <div 
          className="absolute inset-0 animate-breathe"
          style={{
            background: 'radial-gradient(circle at center, rgba(251, 191, 36, 0.2) 0%, rgba(245, 158, 11, 0.4) 50%, rgba(217, 119, 6, 0.6) 100%)',
            backdropFilter: 'blur(40px)'
          }}
        />

        {/* Camada 4: Shimmer wave effect */}
        <div className="absolute inset-0 overflow-hidden">
          <div 
            className="absolute inset-0 animate-shimmer-wave"
            style={{
              background: 'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.15) 50%, transparent 100%)',
              width: '100%',
              height: '100%'
            }}
          />
        </div>

        {/* Conteúdo central */}
        <div className="absolute inset-0 flex flex-col items-center justify-center p-6">
          {/* Container do loader */}
        </div>

        {/* Borda com glow */}
        <div 
          className="absolute inset-0 rounded-2xl pointer-events-none"
          style={{
            border: '1px solid rgba(251, 191, 36, 0.4)',
            boxShadow: 'inset 0 0 60px rgba(251, 191, 36, 0.1)'
          }}
        />
      </div>
    );
  }

  // ============================================
  // ESTADO: FAILED (Falha)
  // ============================================
  if (content.status === 'failed') {
    return (
      <div
        className={`${gridSpan.col} ${gridSpan.row} bg-red-50 border-2 border-red-200 rounded-2xl flex flex-col items-center justify-center p-6 min-h-[200px]`}
      >
        <div className="text-center">
          <div className="w-12 h-12 mx-auto mb-3 bg-red-100 rounded-xl flex items-center justify-center">
            <AlertCircle className="w-6 h-6 text-red-600" />
          </div>
          <div className="flex items-center justify-center gap-2 mb-2">
            <p className="text-red-700 font-semibold text-sm">Falha na geração</p>
          </div>
          {content.error_message && (
            <p className="text-red-600 text-xs mb-3 line-clamp-2">{content.error_message}</p>
          )}
        </div>
      </div>
    );
  }

  // ============================================
  // ESTADO: COMPLETED (Pronto)
  // ============================================
  return (
    <div
      className={`${gridSpan.col} ${gridSpan.row} bg-white rounded-2xl shadow-sm border-2 border-gray-200 overflow-hidden relative group cursor-pointer transition-all hover:shadow-lg hover:border-amber-200 min-h-[200px]`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onPreview && onPreview(content)}
    >
      {/* Conteúdo (Imagem ou Vídeo) */}
      {content.type === 'video' ? (
        <div className="w-full h-full relative bg-black">
          <video
            src={content.result_url}
            className="w-full h-full object-cover"
            muted
            loop
            playsInline
            onMouseEnter={(e) => e.target.play()}
            onMouseLeave={(e) => e.target.pause()}
          />
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-12 h-12 bg-white/90 backdrop-blur-sm rounded-xl flex items-center justify-center">
              <Play className="w-6 h-6 text-gray-900 ml-0.5" />
            </div>
          </div>
        </div>
      ) : (
        <div className="w-full h-full relative bg-gray-100">
          {!imageError ? (
            <img
              src={content.result_url}
              alt={`Content ${content.id}`}
              /* object-contain: posts verticais (Feed Novo 4:5 e Stories 9:16)
                 apareciam CORTADOS nas pontas com object-cover na célula do grid */
              className="w-full h-full object-contain"
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <ImageIcon className="w-12 h-12 text-gray-400" />
            </div>
          )}
        </div>
      )}

      {/* ✅ Fase 4: Overlay persistente "Editando…" (edição IA em andamento) */}
      {isEditingNow && (
        <div className="absolute inset-0 bg-black/60 backdrop-blur-[2px] flex flex-col items-center justify-center gap-2 z-20 pointer-events-none">
          <Loader2 className="w-8 h-8 text-white animate-spin" />
          <span className="text-white text-sm font-semibold">Editando com IA…</span>
          <span className="absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-500 text-white text-[10px] font-bold animate-pulse">
            <Sparkles className="w-3 h-3" /> EDITANDO
          </span>
        </div>
      )}

      {/* Overlay com ações (aparece no hover) */}
      {isHovered && !isEditingNow && (
        <div className="absolute inset-0 bg-black/80 backdrop-blur-sm flex flex-col justify-between p-4 animate-fadeIn">
          {/* Topo: Badge do modelo + Botão de Editar */}
          <div className="flex justify-between items-start">
            
            {/* Botão de Editar Minimalista - Placid/Creatomate/Templated/React */}
            {isEditable && batchId && (
              <button
                onClick={handleEdit}
                className="p-2.5 bg-white/90 hover:bg-white rounded-xl transition-all transform hover:scale-105 shadow-lg"
                title="Editar post"
              >
                <Edit className="w-4 h-4 text-purple-600" />
              </button>
            )}

            {/* ✅ Fase 4: Editar com IA (chat em tela dedicada) — apenas gpt_image */}
            {isGptImageEditable && batchId && (
              <button
                onClick={(e) => { e.stopPropagation(); navigate(`/contents/batch/${batchId}/edit-gpt-image/${content.id}`); }}
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-emerald-500/95 hover:bg-emerald-500 text-white rounded-xl transition-all transform hover:scale-105 shadow-lg"
                title="Editar com IA"
              >
                <Sparkles className="w-4 h-4" />
                <span className="text-xs font-semibold">Editar com IA</span>
              </button>
            )}
          </div>

          {/* Centro: Info + botão explícito de abrir (antes só dava pra abrir
              clicando "no vazio" do overlay — usuário reclamou que não conseguia) */}
          <div className="text-white text-center">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onPreview && onPreview(content); }}
              className="mb-3 inline-flex items-center gap-2 px-4 py-2.5 bg-white/95 hover:bg-white text-gray-900 rounded-xl font-semibold text-sm shadow-lg transition-all transform hover:scale-105"
              title="Abrir em tamanho real"
            >
              <Eye className="w-4 h-4" />
              Abrir
            </button>
            <div className="flex items-center justify-center gap-2 mb-1">
              {content.type === 'video' ? (
                <Film className="w-4 h-4" />
              ) : (
                <ImageIcon className="w-4 h-4" />
              )}
              <p className="font-semibold text-base">
                {content.type === 'video' ? 'Vídeo' : 'Imagem'}
              </p>
            </div>
            {content.content_type_category && (
              <p className="text-xs opacity-80 capitalize">
                {content.content_type_category}
              </p>
            )}
            {/* Indicador de editado */}
            {hasEditHistory && (
              <p className="text-xs text-amber-400 mt-1 flex items-center justify-center gap-1">
                <Pencil className="w-3 h-3" />
                {content.edit_history.length} {content.edit_history.length === 1 ? 'edição' : 'edições'}
              </p>
            )}
          </div>

          {/* Baixo: Ações */}
          <div className="space-y-2">
            {/* Botão de Publicar */}
            {onPublish && (
              <button
                onClick={handlePublish}
                className="w-full bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-500 hover:to-orange-600 text-white font-semibold py-2.5 px-4 rounded-xl flex items-center justify-center gap-2 transition-all transform hover:scale-[1.02] shadow-lg text-sm"
                title="Publicar nas redes sociais"
              >
                <Send className="w-4 h-4" />
                Publicar nas Redes
              </button>
            )}
            
            {/* Outras ações */}
            <div className="flex gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onPreview && onPreview(content);
                }}
                className="flex-1 bg-white/90 backdrop-blur-sm hover:bg-white text-gray-900 font-medium py-2 px-3 rounded-xl flex items-center justify-center gap-2 transition-all text-sm"
                title="Visualizar"
              >
                <Eye className="w-4 h-4" />
                Ver
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownload();
                }}
                className="bg-white/90 backdrop-blur-sm hover:bg-white text-gray-900 font-medium py-2 px-2.5 rounded-xl transition-all"
                title="Download"
              >
                <Download className="w-4 h-4" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopyUrl();
                }}
                className="bg-white/90 backdrop-blur-sm hover:bg-white text-gray-900 font-medium py-2 px-2.5 rounded-xl transition-all"
                title="Copiar link"
              >
                <LinkIcon className="w-4 h-4" />
              </button>
              {onDelete && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(content);
                  }}
                  className="bg-red-500/90 backdrop-blur-sm hover:bg-red-600 text-white font-medium py-2 px-2.5 rounded-xl transition-all"
                  title="Remover"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Badges quando não hover */}
      {!isHovered && (
        <div className="absolute top-2 right-2 flex flex-col gap-1.5 items-end">
          {/* Badge de conclusão */}
          <span className="bg-green-500 text-white px-2.5 py-1 rounded-xl text-xs font-semibold shadow-sm flex items-center gap-1">
            <CheckCircle className="w-3 h-3" />
            Pronto
          </span>
          
          {/* Badge de editado */}
          {hasEditHistory && (
            <span className="bg-purple-500 text-white px-2 py-1 rounded-lg text-xs font-semibold shadow-sm flex items-center gap-1">
              <Pencil className="w-3 h-3" />
              Editado
            </span>
          )}
          
          {/* Badge Placid (editável) */}
          {isPlacidContent && !hasEditHistory && (
            <span className="bg-indigo-500 text-white px-2 py-1 rounded-lg text-xs font-semibold shadow-sm flex items-center gap-1">
              <Edit className="w-3 h-3" />
              Editável
            </span>
          )}
          
          {/* Badges de publicação */}
          {isPublishedOnInstagram && (
            <span className="bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 text-white px-2 py-1 rounded-lg text-xs font-semibold shadow-sm flex items-center gap-1">
              <InstagramIcon className="w-3 h-3" />
            </span>
          )}
          {isPublishedOnTikTok && (
            <span className="bg-black text-white px-2 py-1 rounded-lg text-xs font-semibold shadow-sm flex items-center gap-1">
              <TikTokIcon className="w-3 h-3" />
            </span>
          )}
        </div>
      )}

      {/* Indicador de publicado no canto inferior (quando não hover e publicado) */}
      {!isHovered && isPublishedAnywhere && (
        <div className="absolute bottom-2 left-2">
          <span className="bg-white/95 backdrop-blur-sm text-gray-700 px-2 py-1 rounded-lg text-xs font-medium shadow-sm flex items-center gap-1.5">
            <Send className="w-3 h-3 text-amber-500" />
            Publicado
          </span>
        </div>
      )}
    </div>
  );
};

export default ContentCard;